import React, { useState, useEffect, useRef } from 'react';
import './Marquee.css';

const Patients = () => {
    const [patients, setPatients] = useState([]);
    const [latestPatient, setLatestPatient] = useState(null);
    const [audioQueue, setAudioQueue] = useState([]); // Cola de reproducción
    const [isPlaying, setIsPlaying] = useState(false); // Estado de reproducción
    const [marqueeMessage, setMarqueeMessage] = useState("ACTUALICE SU INFORMACIÓN PERSONAL EN SOME");
    const [animationKey, setAnimationKey] = useState(0); // Clave para reiniciar animación
    const [currentTime, setCurrentTime] = useState(new Date());
    const lastCalledPatientIdRef = useRef(null);
    const marqueeMessageRef = useRef(marqueeMessage);
    const URL = 'https://pct.saludquemchi.cl/events'; //http://localhost:3003/events //https://pct.saludquemchi.cl/events
    const msg0 = 'Llamado a box';
    const msg1 = 'Se presentó';
    const msg2 = 'Diríjase a SOME';

    const getFontSizeForName = (name) => {
        if (!name) return '8.2rem'; // Tamaño predeterminado si no hay nombre
        if (name.length > 30) return '5.5rem'; // Muy largo
        if (name.length > 20) return '8.2rem'; // Largo medio
        return '8.2rem'; // Normal
    };

    useEffect(() => {
        const playAudioQueue = async () => {
            if (!isPlaying && audioQueue.length > 0) {
                setIsPlaying(true); // Marca que un sonido está en reproducción
                const { type, payload } = audioQueue[0]; // Obtiene el primer sonido en cola

                try {
                    if (type === 'alert') {
                        const alertSound = new Audio('alert.mp3'); // Ruta del sonido de alerta
                        await alertSound.play();

                        // Agrega un retraso de 1 segundo antes de la próxima acción
                        await new Promise((resolve) => setTimeout(resolve, 1000)); // 1000ms = 1 segundo
                    } else if (type === 'read') {
                        // Llama a la función de síntesis de voz
                        await readPatientInfo(payload.name, payload.boxName, payload.hallName);
                    }
                } catch (error) {
                    console.error('Error al reproducir sonido:', error);
                } finally {
                    // Remueve el sonido reproducido de la cola
                    setAudioQueue((queue) => queue.slice(1));
                    setIsPlaying(false); // Libera para permitir el siguiente
                }
            }
        };
        playAudioQueue();
    }, [audioQueue, isPlaying]);

    // Maneja la llegada de datos nuevos (conexión única al montar)
    useEffect(() => {
        const eventSource = new EventSource(URL);
        eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);

                if (Array.isArray(data)) {
                    setPatients(data);
                    setLatestPatient(data[0]);

                    if (data[0].status === 0 && data[0].id !== lastCalledPatientIdRef.current) {
                        // Agregar sonidos a la cola
                        setAudioQueue((queue) => [
                            ...queue,
                            { type: 'alert' }, // Sonido de alerta
                            {
                                type: 'read',
                                payload: {
                                    name: data[0].name_patient,
                                    boxName: data[0].boxName,
                                    hallName: data[0].hallName,
                                },
                            },
                        ]);
                        lastCalledPatientIdRef.current = data[0].id;
                    }
                } else if (data.message && data.message !== marqueeMessageRef.current) {
                    // Actualiza el mensaje del marquee y reinicia la animación
                    setMarqueeMessage(data.message);
                    marqueeMessageRef.current = data.message;
                    setAnimationKey((prevKey) => prevKey + 1); // Cambia la clave para reiniciar la animación
                }
            } catch (error) {
                console.error('Error al procesar datos SSE:', error);
            }
        };

        return () => {
            eventSource.close(); // Limpia la conexión al desmontar el componente
        };
    }, []); // Sin dependencias: la conexión SSE se crea una sola vez

    const readPatientInfo = (name, boxName, hallName) => {
        return new Promise((resolve) => {
            if ('speechSynthesis' in window) {
                const text = `Atención, ${name}, presentarse en ${hallName}, ${boxName}`;
                const utterance = new SpeechSynthesisUtterance(text);
                utterance.voice = getRandomSpanishVoice();
                utterance.rate = 0.9;

                utterance.onend = () => resolve();
                window.speechSynthesis.speak(utterance);
            } else {
                console.warn('La síntesis de voz no está soportada en este navegador.');
                resolve();
            }
        });
    };

    // Obtiene una voz aleatoria en español
    const getRandomSpanishVoice = () => {
        const voices = window.speechSynthesis.getVoices();
        const latinAmericanVoices = voices.filter((voice) => 
            voice.lang.startsWith('es-CL')
    );
        if (latinAmericanVoices.length > 0) {
            const randomIndex = Math.floor(Math.random() * latinAmericanVoices.length);
            return latinAmericanVoices[randomIndex];
        }
        return null;
    };

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    // Asegura la inicialización de las voces
    useEffect(() => {
        if (window.speechSynthesis.getVoices().length === 0) {
            window.speechSynthesis.onvoiceschanged = () => {
                getRandomSpanishVoice();
            };
        }
    }, []);

    return (
        <div className="patients-container">
            <div style={{ position: 'absolute', top: '15px', right: '30px', fontSize: '4rem', fontWeight: 'bold' }}>
                {currentTime.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
            </div>
            {latestPatient && (
                <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                    <h1 style={{ fontSize: '3rem', margin: '20px', padding: '1px' }}>Último usuario(a)</h1>
                    <h1 style={{ fontSize: getFontSizeForName(latestPatient.name_patient), margin: '5px 0', padding: '0' }}>
                        {latestPatient.name_patient}
                    </h1>
                    <h2 style={{ fontSize: '6rem', margin: '5px 0', padding: '0' }}>
                        {latestPatient.hallName} - {latestPatient.boxName}
                    </h2>
                </div>
            )}

            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    padding: '10px 0',
                }}
            >
                <table
                    style={{
                        width: '95%',
                        tableLayout: 'auto',
                        borderCollapse: 'separate',
                        borderSpacing: '8px 8px',
                        margin: '0 auto',
                        textAlign: 'center',
                    }}
                >
                    <thead>
                        <tr>
                            <th style={{ padding: '8px', fontSize: '2rem', textAlign: 'center', width: '30%' }}>Nombre</th>
                            <th style={{ padding: '8px', fontSize: '2rem', textAlign: 'center' }}>Hora</th>
                            <th style={{ padding: '8px', fontSize: '2rem', textAlign: 'center' }}>Repetición</th>
                            <th style={{ padding: '8px', fontSize: '2rem', textAlign: 'center' }}>Pasillo</th>
                            <th style={{ padding: '8px', fontSize: '2rem', textAlign: 'center' }}>Box</th>
                            <th style={{ padding: '8px', fontSize: '2rem', textAlign: 'center' }}>Estado</th>
                        </tr>
                    </thead>
                    <tbody>
                        {patients.map((patient) => {
                            const formattedHour = patient.hour.split(':').slice(0, 2).join(':');
                            const currentFormattedHour = patient.repeat_call.split(':').slice(0, 2).join(':');
                            return (
                                <tr key={patient.id} style={{ backgroundColor: 'transparent' }}>
                                    <td style={{ padding: '8px', fontSize: '2.5rem', fontWeight: 'bold', textAlign: 'left' }}>
                                        {patient.name_patient.length > 23
                                            ? `${patient.name_patient.substring(0, 23)}...`
                                            : patient.name_patient}
                                    </td>
                                    <td style={{ padding: '7px', fontSize: '3rem', fontWeight: 'bold', textAlign: 'center' }}>{formattedHour}</td>
                                    <td style={{ padding: '7px', fontSize: '3rem', fontWeight: 'bold', textAlign: 'center' }}>{currentFormattedHour}</td>
                                    <td style={{ padding: '7px', fontSize: '3rem', fontWeight: 'bold', textAlign: 'center' }}>{patient.hallName}</td>
                                    <td style={{ padding: '7px', fontSize: '3rem', fontWeight: 'bold', textAlign: 'center' }}>{patient.boxName}</td>
                                    <td
                                        style={{
                                            padding: '8px',
                                            fontSize: '2.1rem',
                                            backgroundColor:
                                                patient.status === 0
                                                    ? 'dodgerblue'
                                                    : patient.status === 1
                                                    ? 'green'
                                                    : 'grey',
                                            fontWeight: 'bold',
                                            textAlign: 'center',
                                            borderRadius: '20px',
                                        }}
                                    >
                                        {patient.status === 0 ? msg0 : patient.status === 1 ? msg1 : msg2}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div className="marquee-container">
                <div className="marquee">-{marqueeMessage}</div>
                <div className="marquee">-{marqueeMessage}</div>
                <div className="marquee">-{marqueeMessage}</div>
            </div>
        </div>
    );
};

export default Patients;
